import {
  IocSelectorOption,
  IocSelectorService,
} from './../../../../_shared/components/ioc-selector/ioc-selector.service';
import { Injectable, inject } from '@angular/core';
import { Match, Side } from '../../../../_shared/models/match.model';
import {
  ScoreFormat,
  SetType,
} from '../../../../_shared/models/score-format.model';
import { BoardHelpService } from '../../board-help.service';
import {
  Point,
  Server,
  PlayerInfo,
  PlayerCourtPosition,
} from '../../../../_shared/models/point.model';
import { ServerPickleballOrder } from '../../../../_shared/models/serverPicklballOrder.model';
import { PickleballOrder } from '../../../../_shared/models/enums/pickleball-order';
import { PickleballGameFormat } from '../../../../_shared/models/enums/pickleball-game-format';
import { CourtSide } from '../../../../_shared/models/enums/court-side';
import { Score } from '../../../../_shared/models/csTypes/score';

@Injectable({
  providedIn: 'root',
})
export class PickleballTrackerBrainService {
  private iocSelectorService = inject(IocSelectorService);
  private side1PlayerPosition: PlayerCourtPosition = {};
  private side2PlayerPosition: PlayerCourtPosition = {};

  constructor(private bhs: BoardHelpService) {}

  async selectServer(
    match: Match,
    showCancelButton: boolean,
    serverNumber?: number,
    sideNumber?: number | undefined
  ): Promise<Server | undefined> {
    const serverTitle: string = 'Select server';
    const players = this.getPlayers(match);
    const serverOptions = this.getServerOptions(match, players, serverNumber, sideNumber);

    let selectedServer: Server = await this.iocSelectorService.openPopover(
      serverTitle,
      serverOptions,
      showCancelButton
    );

    if (!selectedServer) return undefined;

    const returnerPlayerNumber = match.matchType == 'D' ? await this.selectReturner(
      match,
      selectedServer?.returnerSideNumber,
      showCancelButton
    ): 1;

    if (!returnerPlayerNumber) return undefined;

    selectedServer.returnerPlayerNumber = returnerPlayerNumber;
    this.assignPlayerPositions(players, selectedServer);
    return selectedServer;
  }


  /**
 * Automatically selects the server for singles matches after the first set.
 * 
 * This function is used to determine the server based on the previous set's 
 * server side, following the rule that the serve alternates between sets.
 * 
 * It retrieves the appropriate player from the specified side, assigns
 * the player positions, and returns the server configuration.
 *
 * @param match - The current match object
 * @param sideNumber - The side number (1 or 2) that should serve in this set
 * @returns A configured Server object for the next set
 */
  async autoSelectServerForSinglesAfterFirstSet(match: Match, sideNumber: number): Promise<Server | undefined> {
    const players = this.getPlayers(match);
    const serverPlayer = players?.[sideNumber]?.[0]

    if (!serverPlayer) {
      return undefined;
    }

    const server = {
      "playerName": serverPlayer.playerName,
      "sideNumber": sideNumber,
      "playerNumber": 1,
      "player": serverPlayer.player,
      "returnerSideNumber": 1,
      "returningSide": CourtSide.DEUCE,
      "returnerPlayerNumber": 1
    }
    this.assignPlayerPositions(players, server);
    return server
  }

  /**
   * In the pickle ball double match, the player position is keep changing and we need to update the player position
   * @param server 
   * @param match 
   * @returns 
   */
  updatePlayerCourtPosition(
    serverInfo: any,
    playerPositionData: any
  ): { side1: PlayerCourtPosition; side2: PlayerCourtPosition } {
    const { sideNumber, player, returningSide } = serverInfo;
    const sideKey = sideNumber === 1 ? 'side1' : 'side2';
    const positionData = playerPositionData[sideKey];
  
    // Determine the player's current court position (ad or deuce)
    const currentPositionKey =
      positionData.ad.player === player ? 'ad' :
      positionData.deuce.player === player ? 'deuce' :
      null;
  
    if (!currentPositionKey) {
      return playerPositionData;
    }
  
    // If the player is in the same position as returningSide, swap their position
    const temp = positionData.ad;
    positionData.ad = positionData.deuce;
    positionData.deuce = temp;
  
    return {
      side1: playerPositionData.side1,
      side2: playerPositionData.side2
    };
  }

  private getPlayers(
    match: Match,
  ): PlayerInfo {
    const players: any = {};

    match.sides.forEach((side, i) => {
      players[i + 1] = side.players.map((player, j) => ({
        playerName: this.bhs.getPlayersNames(
          match,
          side.sideNumber,
          'full',
          j + 1
        ),
        sideNumber: i + 1,
        playerNumber: j + 1,
        player: player.participant?._id || null,
      }));
    });

    return players;
  }

  private getServerOptions(match: Match, players: any, serverNumber?: number, sideNumber?: number) {
    return Object.values(players).flatMap((sidePlayers: any[], i) =>
      sidePlayers.filter(player=> {
        return !sideNumber || player.sideNumber === sideNumber
      }).map((player) => ({
        label: player.playerName,
        value: {
          ...player,
          serverNumber,
          returnerSideNumber: i === 0 ? 2 : 1,
          returningSide: CourtSide.DEUCE,
        },
      }))
    );
  }

  private async selectReturner(
    match: Match,
    returnerSideNumber: number = 1,
    showCancelButton: boolean
  ): Promise<number> {
    const returnerTitle = 'Select returner';
    const returnerOptions = match.sides[returnerSideNumber - 1].players.map(
      (_, i) => ({
        label:
          this.bhs.getPlayersNames(match, returnerSideNumber, 'full', i + 1) ??
          '',
        value: i + 1,
      })
    );

    return this.iocSelectorService.openPopover(
      returnerTitle,
      returnerOptions,
      showCancelButton
    );
  }

  private assignPlayerPositions(players: any, selectedServer: Server): void {    
    [1, 2].forEach((side) => {
      players[side].forEach((player: PlayerInfo) => {
        const isServerOrReturner =
          selectedServer.sideNumber === side
            ? player.playerNumber === selectedServer.playerNumber
            : player.playerNumber === selectedServer.returnerPlayerNumber;

        this[`side${side}PlayerPosition`][isServerOrReturner ? 'deuce' : 'ad'] =
          player;
      });
    });
  }

  public getPlayerPosition(side: number): PlayerCourtPosition | null {
    return this[`side${side}PlayerPosition`] ?? null;
  }

  setServerOrder(match, selectedServer: Server): ServerPickleballOrder[] {
    let serverPickleballOrders =
      this.bhs.getLastSet(match)?.serverPickleballOrders || [];

    serverPickleballOrders = serverPickleballOrders.filter((spo) => {
      if (spo.sideNumber !== selectedServer.sideNumber) {
        return spo;
      }
    });

    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    let numberOfPointForSide =
      !match.sets.length ||
      (lastSet.games.length === 1 && lastGame.points.length === 1)
        ? 0
        : this.bhs.getNextToLastPoint(match)[
            `side${selectedServer.sideNumber}Score`
          ];

    const isOdd = match.score ? numberOfPointForSide % 2 === 1 : false;

    serverPickleballOrders.push({
      sideNumber: selectedServer.sideNumber,
      playerNumber: selectedServer.playerNumber,
      order: isOdd ? PickleballOrder.ODD : PickleballOrder.EVEN,
      player: selectedServer.player,
    });

    const unselectedServerPlayerNumber =
      selectedServer.playerNumber === 1 ? 2 : 1;

    serverPickleballOrders.push({
      sideNumber: selectedServer.sideNumber,
      playerNumber: unselectedServerPlayerNumber,
      order: isOdd ? PickleballOrder.EVEN : PickleballOrder.ODD,
      player:
        match.sides[selectedServer.sideNumber - 1].players[
          unselectedServerPlayerNumber - 1
        ]?.participant?._id,
    });

    return serverPickleballOrders;
  }

  changeServer(
    match: Match,
    scoreFormat: ScoreFormat,
    pointWinnerSideNumber
  ): { server?: Server; suggestServerChange: boolean } {
    if (match.matchType === 'S') {
      return this.changeServerForSingles(match, pointWinnerSideNumber);
    }

    let sideNumber: number;
    let playerNumber: number;
    let player: string;
    let serverNumber: number;
    let suggestServerChange: boolean;

    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    const nextToLastPoint = this.bhs.getNextToLastPoint(match);
    const setType = this.getSetType(match);

    if (
      match.sets.length > 1 &&
      lastSet.games.length === 1 &&
      lastGame.points.length === 1
    ) {
      // If it is the beginning of the second set
      // set the serverNumber for standard set at 2
      // Ask user to choose who is serving first if they play with servers
      if (setType === SetType.STANDARD) {
        serverNumber = 2;
      }

      if (nextToLastGame.points[0].server?.playerNumber) {
        suggestServerChange = true;
      }
    } else if (
      setType === SetType.STANDARD &&
      match.score.server.serverNumber === 1
    ) {
      // IF it is a standard set and serverNumber is 1
      // THEN switch player on the team that is serving
      sideNumber = match.score.server.sideNumber;
      playerNumber = match.score.server.playerNumber === 1 ? 2 : 1;
      player =
        match.sides[sideNumber - 1].players[playerNumber - 1].participant._id;
      serverNumber = 2;
    } else if (
      (setType === SetType.RALLY || setType === SetType.MLP) &&
      nextToLastPoint.server.sideNumber === pointWinnerSideNumber
    ) {
      // IF the set is rally
      // AND the servers won the point
      // THEN change server on the same team
      sideNumber = nextToLastPoint.server.sideNumber;
      playerNumber = nextToLastPoint.server.playerNumber === 1 ? 2 : 1;
      player =
        match.sides[sideNumber - 1].players[playerNumber - 1].participant._id;
    } else {
      // In all other cases change team that is serving
      // Find the next server based on odd and even start of serving
      const isOdd =
        nextToLastPoint[`side${pointWinnerSideNumber}Score`] % 2 === 1;
      const nextServer = lastSet.serverPickleballOrders.find(
        (spo) =>
          spo.order === (isOdd ? PickleballOrder.ODD : PickleballOrder.EVEN) &&
          spo.sideNumber === pointWinnerSideNumber
      );

      sideNumber = pointWinnerSideNumber;

      if (setType === SetType.STANDARD) {
        serverNumber = 1;
      }

      // nextServer cannot be found when the team is serving for the first time in the set
      if (!nextServer) {
        suggestServerChange = true;
      } else {
        playerNumber = nextServer.playerNumber;
        player = nextServer.player;
      }
    }

    const server = {
      sideNumber,
      playerNumber,
      player,
      serverNumber,
    };

    return { server, suggestServerChange };
  }

  private changeServerForSingles(
    match,
    pointWinnerSideNumber
  ): { server; suggestServerChange } {
    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    let suggestServerChange = false;
    let sideNumber: number;
    let playerNumber: number;
    let player: string;

    if (
      match.sets.length > 1 &&
      lastSet.games.length === 1 &&
      lastGame.points.length === 1
    ) {
      // If it is the beginning of the second set
      // set the serverNumber for standard set at 2
      // Ask user to choose who is serving first if they play with servers

      if (nextToLastGame.points[0].server?.playerNumber) {
        suggestServerChange = true;
      }
    } else {
      sideNumber = pointWinnerSideNumber;
      playerNumber = 1;
      player =
        match.sides[sideNumber - 1].players[playerNumber - 1]?.participant?._id;
    }

    const server = {
      sideNumber,
      playerNumber,
      player,
    };

    return { server, suggestServerChange };
  }

  isAddedGame(
    match: Match,
    scoreFormat: ScoreFormat,
    nextToLastPointNumberOfPointsWonForWinner: number,
    nextToLastPointNumberOfPointsWonForLoser: number,
    winningSide: number
  ): boolean {
    const numberOfPointsInGame = this.isLastSet(match, scoreFormat)
      ? scoreFormat.lastSetNumberOfPoints
      : scoreFormat.numberOfPointsInGame;

    const setType = this.getSetType(match);
    const lastPoint = this.bhs.getLastPoint(match);

    if (
      this.isLastSet(match, scoreFormat)
        ? scoreFormat.lastSetGameFormat === PickleballGameFormat.WIN_BY_2
        : scoreFormat.winBy2
    ) {
      // Handle WIN BY 2 sets
      if (
        nextToLastPointNumberOfPointsWonForWinner + 1 >= numberOfPointsInGame &&
        nextToLastPointNumberOfPointsWonForWinner >
          nextToLastPointNumberOfPointsWonForLoser
      ) {
        if (
          ((setType === SetType.STANDARD || setType === SetType.MLP) &&
            lastPoint.winningSide === match.score.server.sideNumber) ||
          setType === SetType.RALLY
        ) {
          return true;
        }
      }
    } else if (
      nextToLastPointNumberOfPointsWonForWinner + 1 ===
      numberOfPointsInGame
    ) {
      // Hanadle score if it is deciding point in sideout set
      if (scoreFormat.setType === SetType.STANDARD) {
        return nextToLastPointNumberOfPointsWonForWinner ===
          nextToLastPointNumberOfPointsWonForLoser ||
          winningSide === match.score?.server?.sideNumber
          ? true
          : false;
      } else {
        // Return true for rally scoring
        return true;
      }
    }

    return false;
  }

  getSetType(match: Match): SetType {
    const scoreFormat = this.bhs.getScoreFormat(
      match.matchFormat,
      match.discipline
    );
    return this.isLastSet(match, scoreFormat)
      ? scoreFormat.lastSetGameType
      : scoreFormat.setType;
  }

  isLastSet(match: Match, scoreFormat: ScoreFormat): boolean {
    return (
      scoreFormat.lastSetNumberOfPoints &&
      match.sets.length === scoreFormat.numberOfSets
    );
  }

  switchToStandardScoringForMlp(
    match: Match,
    nextToLastPointNumberOfPointsWonForWinner: number,
    nextToLastPointNumberOfPointsWonForLoser: number,
    sideNumber: number
  ): boolean {
    return nextToLastPointNumberOfPointsWonForWinner >= 20 &&
      match.score.server.sideNumber !== sideNumber &&
      nextToLastPointNumberOfPointsWonForWinner >
        nextToLastPointNumberOfPointsWonForLoser
      ? true
      : false;
  }

  getStandardSetServerOnChange(
    lastPointServer: Server,
    sides: Side[],
    matchType: string,
    playerPositions:any,
    score: Score | undefined
  ): Server {
    let serverPlayerNumber = 1;
    let returnerPlayerNumber = 1;
    let currentServeSidePlayerPosition:any = {};
    

    if (lastPointServer.serverNumber === 1 && matchType === 'D') {
      serverPlayerNumber = lastPointServer.playerNumber === 1 ? 2 : 1;
      returnerPlayerNumber = lastPointServer.returnerPlayerNumber === 1 ? 2 : 1;

      return {
        ...lastPointServer,
        ...{
          playerNumber: serverPlayerNumber,
          returnerPlayerNumber,
          player:
            sides[lastPointServer.sideNumber - 1].players[
              serverPlayerNumber - 1
            ].participant._id,
          serverNumber: 2,
          returningSide:
            lastPointServer.returningSide === CourtSide.DEUCE
              ? CourtSide.AD
              : CourtSide.DEUCE,
        },
      };
    } else {

      let returningSide = CourtSide.DEUCE
      /**
       * For singles matches, the returning side is determined by the point score.
       * If the point score is odd, the returning side is AD; if even, it is DEUCE.
       */
      if (matchType === 'S') {
        const pointScore:any = lastPointServer.returnerSideNumber === 1
          ? score?.side1PointScore
          : score?.side2PointScore;
        returningSide = pointScore % 2 !== 0 ? CourtSide.AD : CourtSide.DEUCE;
      }
      if (lastPointServer.returningSide === CourtSide.DEUCE) {
        // If the last point is that is played was on a deuce side and the servers are changed then the players keep the same positions
        
        if (matchType === 'D') {
          const currentServerNumber = lastPointServer.returnerSideNumber;
          currentServeSidePlayerPosition = playerPositions[`side${currentServerNumber}`]['deuce'];
          serverPlayerNumber = currentServeSidePlayerPosition.playerNumber; 
          returnerPlayerNumber = lastPointServer.playerNumber || 1;
        }

        return {
          sideNumber: lastPointServer.returnerSideNumber,
          playerNumber: serverPlayerNumber,
          returnerSideNumber: lastPointServer.sideNumber,
          returnerPlayerNumber,
          returningSide,
          serverNumber: 1,
          player:currentServeSidePlayerPosition.player,
        };
      } else {
        if (matchType === 'D') {
          const currentServerNumber = lastPointServer.returnerSideNumber;
          currentServeSidePlayerPosition = playerPositions[`side${currentServerNumber}`]['deuce'];          
          serverPlayerNumber = currentServeSidePlayerPosition.playerNumber; 
          returnerPlayerNumber = lastPointServer.playerNumber === 1 ? 2 : 1;
        }
        return {
          sideNumber: lastPointServer.returnerSideNumber,
          playerNumber: serverPlayerNumber,
          returnerSideNumber: lastPointServer.sideNumber,
          returnerPlayerNumber,
          returningSide,
          serverNumber: 1,
          player:currentServeSidePlayerPosition.player,
        };
      }
    }
  }

  getRallySetServerOnChange(
    lastPoint: Point,
    sides: Side[],
    matchType: string
  ): Server {
    const serverSideNumber = lastPoint.winningSide;
    const returnerSideNumber = lastPoint.server.sideNumber;
    const returningSide: CourtSide =
      (serverSideNumber === 1 && lastPoint.side1Score % 2 === 0) ||
      (serverSideNumber === 2 && lastPoint.side2Score % 2 === 0)
        ? CourtSide.DEUCE
        : CourtSide.AD;
    let serverPlayerNumber: number;
    let returnerPlayerNumber: number;

    if (matchType === 'S') {
      serverPlayerNumber = 1;
      returnerPlayerNumber = 1;
    } else {
      if (
        (lastPoint.server.returningSide === CourtSide.DEUCE &&
          returningSide === CourtSide.DEUCE) ||
        (lastPoint.server.returningSide === CourtSide.AD &&
          returningSide === CourtSide.AD)
      ) {
        serverPlayerNumber = lastPoint.server.returnerPlayerNumber;
        returnerPlayerNumber = lastPoint.server.playerNumber;
      } else {
        serverPlayerNumber =
          lastPoint.server.returnerPlayerNumber === 1 ? 2 : 1;
        returnerPlayerNumber = lastPoint.server.playerNumber === 1 ? 2 : 1;
      }
    }

    return {
      sideNumber: serverSideNumber,
      playerNumber: serverPlayerNumber,
      returnerSideNumber,
      returnerPlayerNumber,
      returningSide,
      player:
        sides[serverSideNumber - 1].players[serverPlayerNumber - 1].participant
          ._id,
    };
  }
}
